/**
 * 自提现统一流程 mixin（2026-06-08）
 *
 * 供 self-withdrawal-panel、withdrawal-list 复用，替代旧 channelBtn/channelPixBtn
 * 跳转 withdrawalDetails/pixDetails 的方式。
 *
 * 流程：
 *   1. proceedWithdraw → GET initForWeb
 *   2. 已绑定？（isChannelBoundWithServer，以 bindInfo 服务端按 uid 判断）
 *        否 → openBindSheetForChannel（bindInfo/save 保存在 channel-bind-sheet 内）
 *        是 → goConfirmPage
 *   3. onBindComplete → 重新 initForWeb → goConfirmPage
 *   4. 确认页 submitWithdraw → applyForWeb → 成功弹窗 → /withdrawal
 *
 * 组件需具备：token, uid, deviceId, lang, coin（或覆盖 getWithdrawCoin）
 * 余额不足：showBalanceTip（自提现页）或 show（withdrawal-list）
 */
import axios from "axios";
import { Toast } from "vant";
import { BIND_TYPE_WITHDRAW, loadBindInfo } from "@/utils/bindInfo";

export default {
  data() {
    return {
      /** 绑定完成后待进入确认页的提现上下文 */
      pendingWithdraw: null,
      showBindSheet: false,
      bindItemId: 0,
      bindChannelName: "",
      bindDetailsObj: {},
      bindSabankList: [],
    };
  },
  methods: {
    /** 统一入口：所有渠道（含 PIX、印尼）均走此流程 */
    proceedWithdraw(item, Aitem) {
      const coin = this.getWithdrawCoin();
      // 余额不足（需美元金额 Aitem.USD）时提示，终止流程
      if (coin < Number(Aitem.USD)) {
        this.onWithdrawInsufficientBalance();
        return;
      }
      axios
        .get(
          this.$serviceIp +
            `api/trade/card/tx/initForWeb?token=${this.token}&uid=${this.uid}&id=${item.id}`,
          { headers: { deviceId: this.deviceId, lang: this.lang } }
        )
        .then((response) => {
          if (response.data.code !== 200) {
            Toast(response.data.message || this.$t("transfer.error"));
            return;
          }
          const payload = response.data.data || {};
          const infos = payload.infos || {};
          const sabankList = payload.sabankList || [];
          // 暂存本次提现上下文，供绑定成功回调使用
          this.pendingWithdraw = { item, Aitem, infos, sabankList };
          // 判断是否已绑定：仅以 bindInfo 服务端记录为准（按当前 uid，避免换账户误判）
          this.isChannelBoundWithServer(item.id).then((bound) => {
            if (bound) {
              this.goConfirmPage(item, Aitem, infos, sabankList);
            } else {
              // 未绑定渠道：弹绑定半层的同时提示先绑定
              Toast(this.$t("guild_bind_channel_toast"));
              this.openBindSheetForChannel(item, infos, sabankList);
            }
          });
        });
    },
    /**
     * 判断渠道是否已绑定：仅以 bindInfo 服务端记录为准（按当前 uid 查询）。
     * 说明：不再 fallback 到 isChannelBound(localStorage)，避免换账户登录时
     * 读到旧账户的本地缓存导致误判为"已绑定"而跳过绑定半层。
     */
    isChannelBoundWithServer(channelId) {
      return loadBindInfo({
        bindType: BIND_TYPE_WITHDRAW,
        bizKey: String(channelId),
        serviceIp: this.$serviceIp,
        token: this.token,
        uid: this.uid,
        deviceId: this.deviceId,
        lang: this.lang,
      })
        .then((vo) => !!(vo && vo.infoJson))
        .catch(() => false);
    },
    /** 跳转确认提现信息页：将对象序列化到 query 传递；档位金额单独透传（兼容 USD/usd） */
    goConfirmPage(item, Aitem, infos, sabankList) {
      // 档位提现金额：后端实际返回小写 usd，优先取 usd，兜底大写 USD
      const amount = Aitem.usd != null ? Aitem.usd : Aitem.USD;
      this.$router.push({
        path: "/self-withdraw-confirm",
        query: {
          obj: JSON.stringify(infos),
          newobj: JSON.stringify(Aitem),
          amount: String(amount != null ? amount : ""),
          id: item.id,
          sabankList: JSON.stringify(sabankList || []),
          channelName: item.name,
          workdays: infos.arrivalWorkdays || 3,
        },
      });
    },
    /** 更换渠道弹窗内「编辑」：仅打开绑定半层，不携带 pendingWithdraw */
    openChannelEdit(item) {
      axios
        .get(
          this.$serviceIp +
            `api/trade/card/tx/initForWeb?token=${this.token}&uid=${this.uid}&id=${item.id}`,
          { headers: { deviceId: this.deviceId, lang: this.lang } }
        )
        .then((response) => {
          if (response.data.code !== 200) {
            Toast(response.data.message || this.$t("transfer.error"));
            return;
          }
          const payload = response.data.data || {};
          // 编辑场景不进入确认页，故清空 pendingWithdraw
          this.pendingWithdraw = null;
          this.openBindSheetForChannel(item, payload.infos || {}, payload.sabankList || []);
        });
    },
    // 打开绑定半层：填充渠道 id、名称、详情与银行列表后展示
    openBindSheetForChannel(item, infos, sabankList) {
      this.bindItemId = item.id;
      this.bindChannelName = item.name;
      this.bindDetailsObj = infos;
      this.bindSabankList = sabankList || [];
      this.showBindSheet = true;
    },
    // 关闭绑定半层：清空待进入确认页的上下文
    closeBindSheet() {
      this.showBindSheet = false;
      if (!this.pendingWithdraw) return;
      this.pendingWithdraw = null;
    },
    /** 绑定成功：刷新 initForWeb 后进入确认页 */
    onBindComplete() {
      this.showBindSheet = false;
      if (!this.pendingWithdraw) return;
      const { item, Aitem } = this.pendingWithdraw;
      this.pendingWithdraw = null;
      // 绑定成功后重新拉取最新 infos（含已绑定状态）
      axios
        .get(
          this.$serviceIp +
            `api/trade/card/tx/initForWeb?token=${this.token}&uid=${this.uid}&id=${item.id}`,
          { headers: { deviceId: this.deviceId, lang: this.lang } }
        )
        .then((response) => {
          if (response.data.code !== 200) return;
          const payload = response.data.data || {};
          this.goConfirmPage(item, Aitem, payload.infos || {}, payload.sabankList || []);
        });
    },
    // 余额不足提示：优先 showBalanceTip，兜底 show
    onWithdrawInsufficientBalance() {
      if (typeof this.showBalanceTip !== "undefined") {
        this.showBalanceTip = true;
        return;
      }
      if (typeof this.show !== "undefined") {
        this.show = true;
      }
    },
    /** 可提现余额；withdrawal-list 可覆盖以兼容 listData.cin */
    getWithdrawCoin() {
      return Number(this.coin);
    },
  },
};
